/**
 * Layout shell for the agntInc tenant.
 *
 * AGNT is an IR/marketing site with no listings flow, so it doesn't want the
 * shared Layout's listings affordances (the bottom Map/Sold/Favourites bar,
 * the search-page hooks, etc.). This mirrors `ExpCommercialLayout` in shape:
 * a thin wrapper around WebVitals + AGNT header + children + AGNT footer.
 *
 * Routes that are AGNT-only (`/terms-of-service`, `/news/investor-news`, …)
 * import this directly instead of the shared `components/layout`. The home
 * page and any future shared-with-other-tenants page stays on `Layout`.
 */

import React, { PropsWithChildren, useEffect } from 'react';
import { useRouter } from 'next/router';

import { AnalyticsController } from 'analytics/analytics-controller';
import InvestorAlertsBar from 'components/agnt-inc/investor-alerts-bar';
import AgntIncFooter from 'components/footer/agnt-inc';
import AgntIncHeader from 'components/header/agnt-inc';
import LoadingSpinner from 'components/loading-spinner';
import { PRIVACY_POLICY_PATHS } from 'components/content-pages/agnt-inc/privacy-policy-paths';
import { useFeaturesContext } from 'contexts/features';
import { useRouterLoading } from 'hooks/use-router-loading';
import { WebVitals } from 'components/web-vitals';

import styles from '../style.module.scss';

/**
 * Routes that render the investor-alerts form inline (panel variant) and
 * therefore should NOT also render the global bar. Add a route here when a
 * page includes its own `<InvestorAlertsForm variant="panel" />`.
 */
const INLINE_ALERTS_PANEL_ROUTES = new Set([
  '/', // home page — CalendarAlerts section has the panel variant inline
]);

const LEGAL_ROUTES = new Set<string>([
  '/terms-of-service',
  ...Object.values(PRIVACY_POLICY_PATHS),
]);

type AgntIncLayoutProps = PropsWithChildren<{
  hideHeader?: boolean;
  hideFooter?: boolean;
  solidHeader?: boolean;
}>;

export default function AgntIncLayout({
  children,
  hideHeader = false,
  hideFooter = false,
  solidHeader = false,
}: AgntIncLayoutProps) {
  const { isLoading } = useRouterLoading();
  const { areDelayedScriptsLoaded } = useFeaturesContext();
  const { pathname } = useRouter();

  useEffect(() => {
    if (areDelayedScriptsLoaded) {
      AnalyticsController?.trackDelayedPageView();
    }
  }, [areDelayedScriptsLoaded]);

  const showAlertsBar = !INLINE_ALERTS_PANEL_ROUTES.has(pathname);

  return (
    <>
      <WebVitals />
      {!hideHeader && <AgntIncHeader forceBackground={isLoading || solidHeader} highlightAlerts={LEGAL_ROUTES.has(pathname)} />}
      {isLoading && (
        <div className={styles.loading}>
          <LoadingSpinner />
        </div>
      )}
      {!isLoading && (
        <div className={styles.page}>
          {children}
        </div>
      )}
      {!hideFooter && showAlertsBar && <InvestorAlertsBar />}
      {!hideFooter && <AgntIncFooter />}
    </>
  );
}
